import React, { useCallback, useEffect, useState } from 'react';
import { GoogleLogin, type CredentialResponse } from '@react-oauth/google';
import { useMsal } from '@azure/msal-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { isMicrosoftLoginConfigured, microsoftLoginRequest } from '../auth/microsoft';
import { useAuth } from '../contexts/AuthContext';
import { sanitizeReturnTo } from '../utils/sanitizeReturnTo';
import { clearPendingLoginRedirect } from '../auth/loginRedirect';
import './LoginPage.css';

const LoginPage: React.FC = () => {
  const { instance } = useMsal();
  const { loginWithSocial, isLoading, error, clearError } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [providerError, setProviderError] = useState<string | null>(null);
  const returnTo = sanitizeReturnTo(searchParams.get('returnTo'));

  useEffect(() => clearPendingLoginRedirect(), []);

  const completeLogin = useCallback(async (provider: 'google' | 'microsoft', idToken: string) => {
    const success = await loginWithSocial(provider, idToken);
    if (success) navigate(returnTo, { replace: true });
  }, [loginWithSocial, navigate, returnTo]);

  const handleGoogleSuccess = useCallback(async (response: CredentialResponse) => {
    setProviderError(null);
    clearError();
    if (!response.credential) {
      setProviderError('Google did not return a credential. Please try again.');
      return;
    }
    await completeLogin('google', response.credential);
  }, [clearError, completeLogin]);

  const handleMicrosoftLogin = useCallback(async () => {
    setProviderError(null);
    clearError();
    if (!isMicrosoftLoginConfigured) {
      setProviderError('Microsoft login is not configured for this environment.');
      return;
    }
    try {
      const response = await instance.loginPopup(microsoftLoginRequest);
      if (!response.idToken) throw new Error('Microsoft did not return an ID token');
      await completeLogin('microsoft', response.idToken);
    } catch (loginError) {
      if ((loginError as { errorCode?: string }).errorCode !== 'user_cancelled') {
        setProviderError('Microsoft login failed. Please try again.');
      }
    }
  }, [clearError, completeLogin, instance]);

  return (
    <main className="login-page">
      <section className="login-story" aria-label="ClaraBI introduction">
        <div className="login-story-content">
          <h1>Turn your data into decisions</h1>
          <p>Connect your business data and build clear,<br /> AI-powered dashboards in minutes.</p>
        </div>
      </section>

      <section className="login-panel">
        <div className="login-card">
          <h2>Sign in to ClaraBI</h2>

          {(providerError || error) && <div className="login-error" role="alert">{providerError || error}</div>}

          <button
            className="microsoft-login-button"
            type="button"
            onClick={handleMicrosoftLogin}
            disabled={isLoading || !isMicrosoftLoginConfigured}
          >
            <span className="microsoft-symbol" aria-hidden="true"><i /><i /><i /><i /></span>
            <span className="microsoft-login-label">Continue with Microsoft</span>
          </button>

          <div className="login-divider"><span>or</span></div>
          <div className="google-login-wrapper">
            <GoogleLogin
              onSuccess={handleGoogleSuccess}
              onError={() => setProviderError('Google login failed. Please try again.')}
              text="continue_with"
              shape="rectangular"
              size="large"
              width="100%"
              theme="outline"
            />
          </div>

          {isLoading && <p className="login-loading" aria-live="polite">Signing in…</p>}
          <p className="login-terms">By continuing, you agree to ClaraBI’s terms and privacy policy.</p>
        </div>
      </section>
    </main>
  );
};

export default LoginPage;